import * as _ from 'lodash-es';
import React, { useEffect } from 'react';
import Button from '../../components/Button';
import Caret from '../../components/Caret';
import PageMeta from '../../utils/PageMeta';
import type { OnboardingSubject } from '../onboardingSlice';
import { OnboardingActions } from '../onboardingSlice';
import useOnboardingData from '../useOnboardingData';
import TutorialModal from './TutorialModal';
import TutorialProgress from './TutorialProgress';
import { useAppDispatch } from '../../app/redux/useAppSelector';

interface TutorialProps {
  steps: {
    title: string;
    message: string;
    beacons?: string[];
    imageUrl: string | null;
    onActive?: () => void;
  }[];
  name: OnboardingSubject;
  onBackToList?: () => void;
  onComplete?: () => void;
  onSkip?: () => void;
  openingTitle?: string;
}

const Tutorial = ({
  steps,
  name,
  onBackToList,
  onComplete,
  onSkip,
  openingTitle,
}: TutorialProps) => {
  const dispatch = useAppDispatch();

  const onboardingData = useOnboardingData(name);
  const { onUpdateStep } = onboardingData;

  const handleComplete = () => {
    dispatch(OnboardingActions.setActiveTutorial(null));
    if (onComplete) {
      onComplete();
    }
    return onboardingData.onComplete();
  };

  const handleSkip = () => {
    dispatch(OnboardingActions.setActiveTutorial(null));
    if (onSkip) {
      onSkip();
    }
    return onboardingData.onSkip();
  };

  const stepIndex = onboardingData.onboarding
    ? onboardingData.onboarding.step
    : 0;

  const step = steps[stepIndex];

  useEffect(() => {
    if (!step) {
      return undefined;
    }

    if (step.onActive) {
      step.onActive();
    }

    dispatch(OnboardingActions.setActiveBeacons(step.beacons));
    return () => {
      dispatch(OnboardingActions.setActiveBeacons([]));
    };
  }, [step, dispatch]);

  if (!step) {
    return null;
  }

  let modal = null;
  const initialStepActionProp = onBackToList
    ? {
        additionalControls: (
          <Button
            icon={<Caret direction="left" />}
            variant="link"
            onClick={onBackToList}
          >
            Back to List
          </Button>
        ),
        ...(onSkip
          ? {
              onSecondaryClick: handleSkip,
              secondaryButtonText: 'Skip',
            }
          : {}),
      }
    : {
        onSecondaryClick: handleSkip,
        secondaryButtonText: 'No, thanks',
      };

  if (stepIndex === 0) {
    modal = (
      <TutorialModal
        onPrimaryClick={() => onUpdateStep(1)}
        primaryButtonText="Let's go"
        {...initialStepActionProp}
        {...step}
        title={openingTitle || step.title}
      />
    );
  } else if (stepIndex === steps.length - 1) {
    modal = (
      <TutorialModal
        onPrimaryClick={handleComplete}
        onSecondaryClick={() => onUpdateStep(stepIndex - 1)}
        primaryButtonText="Done"
        secondaryButtonText="Back"
        additionalControls={
          <Button variant="link" onClick={() => onUpdateStep(0)}>
            Restart
          </Button>
        }
        {...step}
      />
    );
  } else {
    modal = (
      <TutorialModal
        additionalControls={
          <TutorialProgress total={steps.length - 2} at={stepIndex - 1} />
        }
        onPrimaryClick={() => onUpdateStep(stepIndex + 1)}
        onSecondaryClick={() => onUpdateStep(stepIndex - 1)}
        primaryButtonText="Continue"
        secondaryButtonText="Back"
        {...step}
      />
    );
  }

  const imageUrls = _.compact(_.uniq(steps.map(({ imageUrl }) => imageUrl)));

  return (
    <>
      <PageMeta>
        {imageUrls.map((imageUrl) => (
          <link key={imageUrl} rel="preload" as="image" href={imageUrl} />
        ))}
      </PageMeta>
      {modal}
    </>
  );
};

export default Tutorial;
